import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CreditCard, Wallet, AlertTriangle, TrendingUp, Clock, Receipt, Users as UsersIcon, DollarSign, Milk, Package, Truck, Droplet } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SOURCE_INFO = {
  reception: { label: "Leche", icon: Milk, color: "bg-[#1E3A8A] text-white" },
  insumo: { label: "Insumo", icon: Package, color: "bg-purple-600 text-white" },
  purchase: { label: "Compra", icon: Truck, color: "bg-amber-500 text-white" },
  cream_curd_reception: { label: "Crema/Cuajada", icon: Droplet, color: "bg-amber-600 text-white" },
};

const METHOD_LABELS = { efectivo: "Efectivo", transferencia: "Transferencia", cheque: "Cheque", otro: "Otro" };

export default function CuentasPorPagar() {
  const [data, setData] = useState({ kpis: {}, items: [], by_supplier: [] });
  const [tab, setTab] = useState("por_documento");
  const [payFor, setPayFor] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("");

  const load = async () => {
    const { data } = await api.get("/accounts-payable/summary");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const kpis = data.kpis || {};
  const items = sourceFilter ? (data.items || []).filter((i) => i.source_type === sourceFilter) : data.items || [];
  const reconciliationItems = items.filter((i) => i.needs_reconciliation);
  const debtItems = items.filter((i) => !i.needs_reconciliation);

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="cxp-page">
      <div>
        <h1 className="font-outfit font-bold text-3xl tracking-tight text-stone-900 dark:text-stone-100">Cuentas por pagar</h1>
        <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">Vista unificada de todos tus adeudos: leche, crema/cuajada, insumos y compras generales.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPI icon={Wallet} label="Total por pagar" value={money(kpis.total_pending || 0)} sub={`${kpis.pending_count || 0} documentos`} tone="blue" />
        <KPI icon={AlertTriangle} label="Vencido" value={money(kpis.overdue_amount || 0)} sub={`${kpis.overdue_count || 0} vencidos`} tone="red" />
        <KPI icon={Clock} label="Por vencer ≤ 7 días" value={money(kpis.due_soon_amount || 0)} sub="Prioridad de pago" tone="amber" />
        <KPI icon={TrendingUp} label="Pagado este mes" value={money(kpis.paid_this_month || 0)} sub="Total salidas" tone="emerald" />
      </div>

      {reconciliationItems.length > 0 && (
        <Card className="p-4 border-orange-200 dark:border-orange-900 bg-orange-50 dark:bg-orange-950/20" data-testid="reconciliation-banner">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-orange-700 dark:text-orange-400" />
            <span className="font-outfit font-semibold text-sm text-orange-800 dark:text-orange-300">{reconciliationItems.length} compra(s) de contado sin cuenta de origen registrada</span>
          </div>
          <p className="text-xs text-orange-700 dark:text-orange-400 mb-3">Ya se le pagó al proveedor, pero falta indicar de qué cuenta de Tesorería salió el efectivo para que quede reflejado ahí.</p>
          <div className="space-y-1.5">
            {reconciliationItems.map((it) => (
              <div key={`${it.source_type}-${it.source_id}`} data-testid={`reconcile-row-${it.source_folio}`} className="flex items-center justify-between gap-3 bg-white dark:bg-stone-950 rounded-md px-3 py-2 border border-orange-100 dark:border-orange-900/60">
                <div className="text-xs">
                  <span className="font-mono font-medium text-[#1E3A8A] dark:text-blue-400">{it.source_folio}</span>
                  <span className="text-stone-500 dark:text-stone-400"> · {it.supplier_name} · {money(it.total)}</span>
                </div>
                <Button size="sm" onClick={() => setAssignFor(it)} data-testid={`assign-account-${it.source_folio}`} className="h-7 text-xs bg-orange-600 hover:bg-orange-700 text-white">Asignar cuenta</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex gap-1 border-b border-stone-200 dark:border-stone-800">
        <TabBtn active={tab === "por_documento"} onClick={() => setTab("por_documento")} icon={Receipt} label="Por documento" testId="tab-por-doc" />
        <TabBtn active={tab === "por_proveedor"} onClick={() => setTab("por_proveedor")} icon={UsersIcon} label="Por proveedor" testId="tab-por-prov" />
      </div>

      {tab === "por_documento" && (
        <>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setSourceFilter("")} className={`text-xs px-3 py-1.5 rounded-md font-outfit font-medium ${sourceFilter === "" ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900" : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"}`}>Todos ({data.items?.length || 0})</button>
            {Object.entries(SOURCE_INFO).map(([k, info]) => {
              const count = (data.items || []).filter((i) => i.source_type === k).length;
              const Icon = info.icon;
              return (
                <button key={k} onClick={() => setSourceFilter(k)} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-outfit font-medium ${sourceFilter === k ? info.color : "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300"}`}>
                  <Icon className="w-3 h-3" /> {info.label} ({count})
                </button>
              );
            })}
          </div>
          <ItemsTable items={debtItems} onPay={setPayFor} />
        </>
      )}

      {tab === "por_proveedor" && <SuppliersTable suppliers={data.by_supplier || []} />}

      <PaymentDialog item={payFor} onClose={() => setPayFor(null)} onSaved={async () => { setPayFor(null); await load(); }} />
      <AssignAccountDialog item={assignFor} onClose={() => setAssignFor(null)} onSaved={async () => { setAssignFor(null); await load(); }} />
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId} className={`px-4 py-2.5 text-sm font-outfit font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${active ? "border-[#1E3A8A] text-[#1E3A8A] dark:text-blue-400 dark:border-blue-400" : "border-transparent text-stone-500 hover:text-stone-800 dark:hover:text-stone-200"}`}>
    <Icon className="w-4 h-4" strokeWidth={1.75} />
    {label}
  </button>
);

const ItemsTable = ({ items, onPay }) => (
  <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
          <tr>{["Estado", "Origen", "Folio/Ref", "Proveedor", "Detalle", "Fecha", "Vence", "Total", "Pagado", "Saldo", "Acciones"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={11} className="px-4 py-12 text-center text-sm text-stone-500">Sin adeudos pendientes con los filtros aplicados.</td></tr>
          ) : items.map((it) => {
            const info = SOURCE_INFO[it.source_type];
            const Icon = info?.icon;
            return (
              <tr key={`${it.source_type}-${it.source_id}`} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50" data-testid={`ap-row-${it.source_folio}`}>
                <td className="px-3 py-3">
                  {it.overdue
                    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 dark:bg-red-950/50 text-red-800 dark:text-red-300 text-[10px] uppercase tracking-wider font-outfit font-semibold"><AlertTriangle className="w-3 h-3" /> Vencido {it.days_overdue}d</span>
                    : <span className="inline-block px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 text-[10px] uppercase tracking-wider font-outfit font-semibold">Al día</span>}
                </td>
                <td className="px-3 py-3"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-outfit font-semibold ${info?.color}`}>{Icon && <Icon className="w-3 h-3" />} {info?.label}</span></td>
                <td className="px-3 py-3 text-sm font-medium text-[#1E3A8A] dark:text-blue-400 font-mono">{it.source_folio}</td>
                <td className="px-3 py-3 text-sm">
                  <div className="font-medium text-stone-900 dark:text-stone-100">{it.supplier_name}</div>
                  {it.supplier_phone && <div className="text-[10px] text-stone-500 dark:text-stone-400 font-mono">{it.supplier_phone}</div>}
                </td>
                <td className="px-3 py-3 text-xs text-stone-600 dark:text-stone-400 max-w-[240px] truncate">{it.detail || "—"}</td>
                <td className="px-3 py-3 text-xs tabular-nums text-stone-500">{it.date}</td>
                <td className="px-3 py-3 text-xs tabular-nums"><span className={it.overdue ? "text-red-600 dark:text-red-400 font-semibold" : "text-stone-700 dark:text-stone-300"}>{it.due_date || "—"}</span></td>
                <td className="px-3 py-3 text-sm tabular-nums text-stone-700 dark:text-stone-300">{money(it.total)}</td>
                <td className="px-3 py-3 text-sm tabular-nums text-emerald-700 dark:text-emerald-400">{money(it.amount_paid)}</td>
                <td className="px-3 py-3 text-sm tabular-nums font-semibold text-amber-700 dark:text-amber-400">{money(it.balance)}</td>
                <td className="px-3 py-3">
                  <Button size="sm" onClick={() => onPay(it)} data-testid={`ap-pay-${it.source_folio}`} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white">
                    <DollarSign className="w-3.5 h-3.5 mr-1" /> Pagar
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Card>
);

const SuppliersTable = ({ suppliers }) => (
  <Card className="border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-stone-100 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
          <tr>{["Proveedor", "Teléfono", "Docs pendientes", "Saldo total", "Saldo vencido", "Máx días vencido"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">{h}</th>)}</tr>
        </thead>
        <tbody>
          {suppliers.length === 0 ? (
            <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-stone-500">Sin proveedores con saldo pendiente.</td></tr>
          ) : suppliers.map((s, i) => (
            <tr key={i} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/50">
              <td className="px-3 py-3 text-sm font-medium text-stone-900 dark:text-stone-100">{s.supplier_name}</td>
              <td className="px-3 py-3 text-sm text-stone-600 dark:text-stone-400 font-mono">{s.supplier_phone || "—"}</td>
              <td className="px-3 py-3 text-sm tabular-nums text-stone-700 dark:text-stone-300">{s.items_count}</td>
              <td className="px-3 py-3 text-sm tabular-nums font-semibold text-amber-700 dark:text-amber-400">{money(s.total_balance)}</td>
              <td className="px-3 py-3 text-sm tabular-nums">{s.overdue_balance > 0 ? <span className="text-red-600 dark:text-red-400 font-semibold">{money(s.overdue_balance)}</span> : <span className="text-stone-400">—</span>}</td>
              <td className="px-3 py-3 text-sm tabular-nums">{s.max_days_overdue > 0 ? <span className="text-red-600 dark:text-red-400 font-semibold">{s.max_days_overdue} días</span> : <span className="text-stone-400">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

const PaymentDialog = ({ item, onClose, onSaved }) => {
  const [form, setForm] = useState({ amount: "", method: "transferencia", payment_date: new Date().toISOString().slice(0, 10), reference: "", notes: "", account_id: "" });
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    if (item) {
      setForm({ amount: item.balance?.toString() || "", method: "transferencia", payment_date: new Date().toISOString().slice(0, 10), reference: "", notes: "", account_id: "" });
      api.get("/treasury/accounts").then((r) => setAccounts(r.data.filter((a) => a.active !== false))).catch(() => {});
    }
  }, [item]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Monto inválido");
    if (amt > (item.balance + 0.009)) return toast.error(`El monto supera el saldo (${money(item.balance)})`);
    try {
      const payload = { ...form, amount: amt, account_id: form.account_id || null };
      const { data } = await api.post(`/accounts-payable/${item.source_type}/${item.source_id}/payments`, payload);
      const suf = form.account_id ? " · movimiento a tesorería registrado" : "";
      toast.success(`Pago registrado. Nuevo saldo: ${money(data.balance)}${suf}`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-outfit">Registrar pago · {item?.source_folio}</DialogTitle>
          <DialogDescription>{item?.supplier_name} · Total {money(item?.total)} · Ya pagado {money(item?.amount_paid)} · <span className="font-semibold text-amber-700 dark:text-amber-400">Saldo {money(item?.balance)}</span></DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Monto *</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="ap-payment-amount" autoFocus />
              <div className="flex gap-1 mt-1.5">
                <button type="button" onClick={() => setForm({ ...form, amount: item?.balance?.toString() })} className="text-[10px] px-2 py-0.5 rounded bg-stone-100 dark:bg-stone-800">Total ({money(item?.balance)})</button>
                <button type="button" onClick={() => setForm({ ...form, amount: (item?.balance / 2)?.toFixed(2) })} className="text-[10px] px-2 py-0.5 rounded bg-stone-100 dark:bg-stone-800">50%</button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Método</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(METHOD_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Fecha</Label>
              <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Referencia</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1.5" placeholder="Núm. transferencia..." />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Cuenta origen (Tesorería, opcional)</Label>
            <Select value={form.account_id || "_none"} onValueChange={(v) => setForm({ ...form, account_id: v === "_none" ? "" : v })}>
              <SelectTrigger className="mt-1.5" data-testid="ap-payment-account"><SelectValue placeholder="Sin registrar en tesorería" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— No registrar en tesorería —</SelectItem>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency} · {money(a.balance || 0)}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-stone-500 mt-1">Si eliges cuenta, el egreso se refleja automáticamente en Tesorería.</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Notas</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" />
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" data-testid="ap-payment-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">Registrar pago</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AssignAccountDialog = ({ item, onClose, onSaved }) => {
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    if (item) {
      setAccountId("");
      api.get("/treasury/accounts").then((r) => setAccounts(r.data.filter((a) => a.active !== false))).catch(() => {});
    }
  }, [item]);

  const submit = async (e) => {
    e.preventDefault();
    if (!accountId) return toast.error("Elige una cuenta");
    try {
      const { data } = await api.post(`/receptions/${item.source_id}/assign-account`, { account_id: accountId });
      toast.success(`Cuenta asignada: ${data.account_name} · movimiento ${data.movement_folio} registrado en Tesorería`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-outfit">Asignar cuenta de origen · {item?.source_folio}</DialogTitle>
          <DialogDescription>{item?.supplier_name} · Pagado de contado · {money(item?.total)}. Elige de qué cuenta salió el efectivo para registrar el movimiento en Tesorería.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">Cuenta de origen *</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="mt-1.5" data-testid="assign-account-select"><SelectValue placeholder="Elegir cuenta…" /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 justify-end pt-2 border-t border-stone-200 dark:border-stone-800">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" data-testid="assign-account-submit" className="bg-orange-600 hover:bg-orange-700 text-white">Asignar y registrar movimiento</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const TONES = {
  blue: "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400",
  amber: "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400",
  emerald: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400",
  red: "bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400",
};

const KPI = ({ icon: Icon, label, value, sub, tone = "blue" }) => (
  <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">{label}</div>
        <div className="mt-2 font-outfit font-semibold text-2xl text-stone-900 dark:text-stone-100 tabular-nums">{value}</div>
        {sub && <div className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{sub}</div>}
      </div>
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${TONES[tone]}`}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
    </div>
  </Card>
);
